/**
 * ヘッダーの通知ベル（ログインユーザー専用）
 *
 * デスクトップ(sm以上)は Headless UI Popover のドロップダウン、
 * モバイルは BottomSheet で通知一覧を表示する。
 */

import React, { useState, useSyncExternalStore } from 'react';
import {
  Popover,
  PopoverButton,
  PopoverPanel,
} from '@headlessui/react';
import { BellIcon } from '@heroicons/react/24/outline';
import { BottomSheet } from '@/components/common/BottomSheet';
import NotificationPanel from '@/components/layout/NotificationPanel';
import { useUnreadNotificationCount } from '@/hooks/useNotifications';

const DESKTOP_QUERY = '(min-width: 640px)';

function subscribeToMediaQuery(callback: () => void): () => void {
  const mql = window.matchMedia(DESKTOP_QUERY);
  mql.addEventListener('change', callback);
  return () => mql.removeEventListener('change', callback);
}

function useIsDesktop(): boolean {
  return useSyncExternalStore(
    subscribeToMediaQuery,
    () => window.matchMedia(DESKTOP_QUERY).matches
  );
}

const bellButtonClass =
  'relative p-2 rounded-lg text-text-600 hover:text-primary-600 hover:bg-text-100 transition-colors';

const UnreadBadge: React.FC<{ count: number }> = ({ count }) => {
  if (count <= 0) return null;
  return (
    <span className="absolute top-0.5 right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger-500 px-1 text-[10px] font-semibold leading-none text-white">
      {count > 9 ? '9+' : count}
    </span>
  );
};

const BellIconWithBadge: React.FC<{ count: number }> = ({ count }) => (
  <>
    <BellIcon className="w-6 h-6" />
    <UnreadBadge count={count} />
    <span className="sr-only">お知らせを開く</span>
  </>
);

const NotificationBell: React.FC = () => {
  const isDesktop = useIsDesktop();
  const { unreadCount } = useUnreadNotificationCount();
  const [sheetOpen, setSheetOpen] = useState(false);

  if (isDesktop) {
    return (
      <Popover className="relative">
        {({ open, close }) => (
          <>
            <PopoverButton className={bellButtonClass}>
              <BellIconWithBadge count={unreadCount} />
            </PopoverButton>
            <PopoverPanel
              transition
              className="absolute right-0 z-50 mt-2 w-96 origin-top-right rounded-xl bg-white shadow-xl ring-1 ring-text-100 transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0"
            >
              <NotificationPanel
                open={open}
                unreadCount={unreadCount}
                onClose={close}
              />
            </PopoverPanel>
          </>
        )}
      </Popover>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setSheetOpen(true)}
        className={bellButtonClass}
      >
        <BellIconWithBadge count={unreadCount} />
      </button>
      <BottomSheet isOpen={sheetOpen} onClose={() => setSheetOpen(false)}>
        <NotificationPanel
          open={sheetOpen}
          unreadCount={unreadCount}
          onClose={() => setSheetOpen(false)}
        />
      </BottomSheet>
    </>
  );
};

export default NotificationBell;
